import {useNavigate, useLocation} from 'react-router-dom';
import {useShopping} from '../contexts/ShoppingContext';
import {useMemo} from 'react';
import {useClientData} from '../hooks/useClientData';

export function CategoryNav() {
    const {state} = useShopping();
    const navigate = useNavigate();
    const location = useLocation();
    const {clientData} = useClientData();
    const themeColors = clientData?.themeColors;

    // Get URL parameters
    const urlParams = new URLSearchParams(location.search);
    const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : null;
    const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : null;

    // Get departments from state
    const departments = state.departments;

    // Group departments and categories for display
    const departmentGroups = useMemo(() => {
        if (!departments) return [];

        // Always show all departments with their categories
        return departments
            .filter(dept => dept.productCategories && dept.productCategories.length > 0)
            .map(dept => ({
                department: dept,
                categories: dept.productCategories || []
            }));
    }, [departments]);

    const handleCategoryClick = (categoryId: number, departmentId: number) => {
        const searchParams = new URLSearchParams(location.search);

        // If clicking on already selected category, deselect it
        if (selectedCategoryId === categoryId && selectedDepartmentId === departmentId) {
            searchParams.delete('categoryId');
        } else {
            searchParams.set('categoryId', categoryId.toString());
            searchParams.set('departmentId', departmentId.toString());
        }

        searchParams.delete('page'); // Reset to page 1 when category changes
        navigate(`/?${searchParams.toString()}`);
    };

    const handleAllProductsClick = () => {
        const searchParams = new URLSearchParams();
        if (selectedDepartmentId) {
            searchParams.set('departmentId', selectedDepartmentId.toString());
        }
        // page is already not included
        navigate(searchParams.toString() ? `/?${searchParams.toString()}` : '/');
    };

    const isActiveCategory = (categoryId: number, departmentId: number) => {
        return selectedCategoryId === categoryId && selectedDepartmentId === departmentId;
    };

    const isAllProductsActive = () => {
        return !selectedCategoryId;
    };

    if (!themeColors) return null;

    return (
        <aside
            className="hidden md:flex md:flex-col w-56 border-r border-gray-600 overflow-y-auto fixed left-0 bottom-0 z-30 transition-colors duration-500"
            style={{
                top: 'var(--header-height, 108px)',
                backgroundColor: themeColors?.left_side_background
            }}
            aria-label="Category navigation"
        >
            <nav className="pt-8 pb-6 px-3 space-y-1">
                {/* Filter Header */}
                <div
                    className="-mx-3 px-3 py-2 mb-2 text-sm font-semibold uppercase tracking-wider"
                    style={{
                        color: themeColors?.header_text_color || '#e5e7eb',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }}
                >
                    FILTER {selectedDepartmentId
                        ? departmentGroups.find(g => g.department.id === selectedDepartmentId)?.department.name.toUpperCase() || 'ALL DEPARTMENTS'
                        : 'ALL DEPARTMENTS'}
                </div>

                <button
                    onClick={handleAllProductsClick}
                    className={`w-full text-left px-3 py-2 text-sm transition-all duration-150 focus:outline-none flex items-center gap-2 ${
                        isAllProductsActive()
                            ? 'font-medium'
                            : ''
                    }`}
                    style={{
                        color: isAllProductsActive()
                            ? themeColors?.left_side_active_color || '#ffffff'
                            : themeColors?.left_side_color || '#e5e7eb'
                    }}
                    aria-current={isAllProductsActive() ? 'page' : undefined}
                >
                    <div
                        className={`w-4 h-4 border-2 rounded transition-all duration-150 flex-shrink-0`}
                        style={{
                            borderColor: isAllProductsActive()
                                ? 'rgba(255, 255, 255)'
                                : themeColors?.left_side_color || '#e5e7eb',
                            backgroundColor: isAllProductsActive()
                                ? 'rgba(255, 255, 255)'
                                : 'transparent'
                        }}
                    />

                    View All Products
                </button>
        {/* Department Groups with Headers */}
        {departmentGroups.map(({ department, categories }) => (
          <div key={department.id} className="mt-4">
            {/* Department Header */}
            <div
              className="-mx-3 px-3 py-2 text-xs font-semibold uppercase tracking-wider"
              style={{
                color: themeColors?.header_text_color || '#e5e7eb',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }}
            >
              {department.name} {department.memo && `(${department.memo})`}
            </div>

            {/* Categories under this department */}        {categories.map(category => (
                    <button
                        key={category.id}
                        onClick={() => handleCategoryClick(category.id, department.id)}
                        className={`w-full text-left px-3 py-2 text-sm transition-all duration-150 focus:outline-none flex items-center gap-2 ${
                            isActiveCategory(category.id, department.id)
                                ? 'font-medium'
                                : ''
                        }`}
                        style={{
                            color: isActiveCategory(category.id, department.id)
                                ? themeColors?.left_side_active_color || '#ffffff'
                                : themeColors?.left_side_color || '#e5e7eb'
                        }}
                        aria-current={isActiveCategory(category.id, department.id) ? 'page' : undefined}
                    >
                        <div
                            className={`w-4 h-4 border-2 rounded transition-all duration-150 flex-shrink-0`}
                            style={{
                                borderColor: isActiveCategory(category.id, department.id)
                                    ? 'rgba(255, 255, 255)'
                                    : themeColors?.left_side_color || '#e5e7eb',
                                backgroundColor: isActiveCategory(category.id, department.id)
                                    ? 'rgba(255, 255, 255)'
                                    : 'transparent'
                            }}
                        />
                        {category.name} {category.memo && `(${category.memo})`}
                    </button>))}

            {/* Navigation Links under this department */}
            {department.navigationLinks && department.navigationLinks.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-600/30">
                {department.navigationLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-left px-3 py-2 text-sm transition-all duration-150 focus:outline-none flex items-center gap-2 hover:opacity-80"
                    style={{
                      color: themeColors?.left_side_color || '#e5e7eb'
                    }}
                  >
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
                ))}
            </nav>
        </aside>
    );
}
